import { useRef } from "react";
import {
  motion,
  useMotionValue,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import { ArrowRight, Play, ShieldCheck } from "lucide-react";
import { fragments } from "../../data/sample";
import { Button, MagneticButton, Pill } from "../primitives";
import { SectionImage } from "../chrome";
import { PhoneMockup } from "../PhoneMockup";

// Orbit positions (px offset from phone center). Pushed out so the chips peek
// AROUND the phone (they render behind it) instead of over its content.
const ORBIT = [
  { x: -195, y: -150 },
  { x: 205, y: -165 },
  { x: 250, y: 20 },
  { x: 190, y: 180 },
  { x: -75, y: 235 },
  { x: -240, y: 120 },
  { x: -255, y: -30 },
];

export function Hero() {
  const ref = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();

  // Pointer parallax
  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const rx = useSpring(useTransform(py, [-0.5, 0.5], [6, -6]), { stiffness: 120, damping: 20 });
  const ry = useSpring(useTransform(px, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 20 });

  // Scroll: fragments flow inward + fade as the hero scrolls away
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const flow = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const fragOpacity = useTransform(scrollYProgress, [0, 0.45], [1, 0]);

  function onMove(e: React.PointerEvent) {
    if (reduce) return;
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    px.set((e.clientX - r.left) / r.width - 0.5);
    py.set((e.clientY - r.top) / r.height - 0.5);
  }

  return (
    <div id="top" ref={ref} className="relative overflow-hidden">
      {/* ambient backdrop */}
      <div className="pointer-events-none absolute inset-0 bg-field-grid opacity-[0.35]" />
      <div className="pointer-events-none absolute -top-40 left-1/2 h-[42rem] w-[42rem] -translate-x-1/2 rounded-full bg-brand/10 blur-[120px]" />
      <div className="pointer-events-none absolute right-0 top-40 h-[30rem] w-[30rem] rounded-full bg-accent/10 blur-[120px]" />

      <div
        className="relative mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-12 px-5 pb-20 pt-32 sm:px-8 md:min-h-screen md:grid-cols-2 md:gap-8 md:pt-28"
        onPointerMove={onMove}
      >
        {/* Left: copy — no opacity fade so the H1 (LCP element) paints instantly */}
        <motion.div
          initial={reduce ? false : { y: 16 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-10"
        >
          <Pill className="mb-5">
            <ShieldCheck className="h-3.5 w-3.5 text-brand" /> Parent-owned · Private by default
          </Pill>
          <h1 className="text-[2.6rem] font-extrabold leading-[1.04] sm:text-6xl">
            The <span className="text-gradient">System of Record</span> for Youth Sports
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-ink-soft sm:text-lg">
            PlayerDNA turns scattered videos, stats, coach feedback, and season history into one
            parent-owned development record for young athletes.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <MagneticButton href="#early-access">
              Join Early Access <ArrowRight className="h-4 w-4" />
            </MagneticButton>
            <Button href="#preview" variant="ghost">
              <Play className="h-4 w-4" /> View Product Preview
            </Button>
          </div>
          <p className="mt-6 text-xs text-ink-muted">
            One athlete. One verified record. Every season.
          </p>
        </motion.div>

        {/* Right: phone + orbiting fragments */}
        <div className="relative flex items-center justify-center md:justify-end">
          <motion.div
            style={reduce ? undefined : { rotateX: rx, rotateY: ry, transformPerspective: 1000 }}
            className="relative"
          >
            {/* cinematic photo sits behind the live phone for depth */}
            <SectionImage
              src="/images/hero.webp"
              alt="Young athlete on a field at dusk"
              priority
              className="absolute -inset-8 -z-10 hidden rotate-1 shadow-[0_24px_60px_-30px_rgba(13,43,95,0.35)] md:block"
            />
            <motion.div
              initial={reduce ? false : { opacity: 0, scale: 0.97, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="relative z-10"
            >
              <PhoneMockup />
            </motion.div>

            {/* fragments orbit then flow into the phone on scroll */}
            {fragments.map((label, i) => {
              const pos = ORBIT[i % ORBIT.length];
              return (
                <Fragment
                  key={label}
                  label={label}
                  pos={pos}
                  flow={flow}
                  opacity={fragOpacity}
                  delay={0.3 + i * 0.07}
                  reduce={!!reduce}
                />
              );
            })}
          </motion.div>
        </div>
      </div>
    </div>
  );
}

function Fragment({
  label,
  pos,
  flow,
  opacity,
  delay,
  reduce,
}: {
  label: string;
  pos: { x: number; y: number };
  flow: ReturnType<typeof useTransform<number, number>>;
  opacity: ReturnType<typeof useTransform<number, number>>;
  delay: number;
  reduce: boolean;
}) {
  const x = useTransform(flow, (f) => pos.x * f);
  const y = useTransform(flow, (f) => pos.y * f);
  return (
    <motion.div
      className="pointer-events-none absolute left-1/2 top-1/2 z-0 hidden md:block"
      style={reduce ? { x: pos.x, y: pos.y } : { x, y, opacity }}
    >
      <motion.span
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay }}
        className="-translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-full border border-line/80 glass px-3 py-1.5 text-[11px] font-medium text-ink-soft shadow-lg"
      >
        {label}
      </motion.span>
    </motion.div>
  );
}
